import type {
  PatientQuoteSummary,
  PatientQuotesDashboardResponse,
  PatientTravelCasePlanResponse,
} from "@jema-manager/shared";
import { apiClient } from "./api-client";
import {
  resolveCurrentTranslationLocale,
  translatePatientQuoteSummary,
  translatePatientQuotesDashboard,
  translatePatientTravelCasePlan,
} from "@shared/lib/translation";

interface QuoteTranslationOptions {
  locale?: string;
}

export const fetchMyQuoteDashboard = async (
  options?: QuoteTranslationOptions,
): Promise<PatientQuotesDashboardResponse> => {
  const response = await apiClient.get<{ data?: PatientQuotesDashboardResponse } & PatientQuotesDashboardResponse>(
    "/patients/me/quotes",
  );
  // Backend wraps in { data: result }; support both shapes so parsing is robust
  const payload =
    response && typeof response === "object" && "data" in response && response.data != null
      ? response.data
      : (response as PatientQuotesDashboardResponse);
  const locale = options?.locale ?? resolveCurrentTranslationLocale();
  return translatePatientQuotesDashboard(payload, locale, "auto");
};

export const fetchMyQuotes = fetchMyQuoteDashboard;

export const fetchQuoteDetail = async (
  quoteId: string,
  options?: QuoteTranslationOptions,
): Promise<PatientQuoteSummary> => {
  const response = await apiClient.get<{ data?: PatientQuoteSummary } & PatientQuoteSummary>(
    `/patients/quotes/${quoteId}`,
  );
  const payload =
    response && typeof response === "object" && "data" in response && response.data != null
      ? response.data
      : (response as PatientQuoteSummary);
  const locale = options?.locale ?? resolveCurrentTranslationLocale();
  return translatePatientQuoteSummary(payload, locale, "auto");
};

export interface QuoteDocumentsResponse {
  documentRequests: Array<{ id: string; label: string; is_required?: boolean; sort_order?: number }>;
  documentSubmissions: Array<{
    id: string;
    service_request_document_request_id: string;
    file_id: string | null;
    submitted_at: string;
    file?: {
      originalFilename: string | null;
      mimeType: string | null;
      downloadUrl: string;
    } | null;
  }>;
}

export const fetchQuoteDocuments = async (quoteId: string): Promise<QuoteDocumentsResponse> => {
  const response = await apiClient.get<{ data?: QuoteDocumentsResponse } & QuoteDocumentsResponse>(
    `/patients/quotes/${quoteId}/documents`,
  );
  const payload =
    response && typeof response === "object" && "data" in response && response.data != null
      ? response.data
      : (response as QuoteDocumentsResponse);
  return payload;
};

export const acceptQuote = async (quoteId: string, selectedPlanKey?: string | null): Promise<PatientQuoteSummary> => {
  const response = await apiClient.post<{ data?: PatientQuoteSummary } & PatientQuoteSummary>(
    `/patients/quotes/${quoteId}/accept`,
    { selectedPlanKey },
  );
  const payload =
    response && typeof response === "object" && "data" in response && response.data != null
      ? response.data
      : (response as PatientQuoteSummary);
  return payload;
};

export const rejectQuote = async (quoteId: string, reason?: string): Promise<PatientQuoteSummary> => {
  const response = await apiClient.post<{ data?: PatientQuoteSummary } & PatientQuoteSummary>(
    `/patients/quotes/${quoteId}/reject`,
    { reason },
  );
  const payload =
    response && typeof response === "object" && "data" in response && response.data != null
      ? response.data
      : (response as PatientQuoteSummary);
  return payload;
};

export const negotiateQuote = async (quoteId: string, notes: string): Promise<PatientQuoteSummary> => {
  const response = await apiClient.post<{ data?: PatientQuoteSummary } & PatientQuoteSummary>(
    `/patients/quotes/${quoteId}/negotiate`,
    { notes },
  );
  const payload =
    response && typeof response === "object" && "data" in response && response.data != null
      ? response.data
      : (response as PatientQuoteSummary);
  return payload;
};

export const fetchTravelCasePlan = async (
  travelCaseId: string,
  options?: QuoteTranslationOptions,
): Promise<PatientTravelCasePlanResponse> => {
  const response = await apiClient.get<
    { data?: PatientTravelCasePlanResponse } & PatientTravelCasePlanResponse
  >(`/patients/me/travel-cases/${encodeURIComponent(travelCaseId)}/plan`);
  const payload =
    response && typeof response === "object" && "data" in response && response.data != null
      ? response.data
      : (response as PatientTravelCasePlanResponse);
  const locale = options?.locale ?? resolveCurrentTranslationLocale();
  return translatePatientTravelCasePlan(payload, locale, "auto");
};
